import { Formik, Form } from 'formik'
import { Persist } from 'formik-persist'
import {
  anyPass,
  propSatisfies,
  equals,
  pathSatisfies,
  propEq,
  complement,
  path,
  values as ramdaValues,
  filter,
  includes,
  mergeRight,
  pipe,
  pick,
  isNil,
  map,
  length,
  toPairs,
  fromPairs,
  isEmpty,
  __
} from 'ramda'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled, { createGlobalStyle } from 'styled-components'
import * as Yup from 'yup'
import { useToast } from '@machobear/design-system'
import { pushToDatalayer } from '@/utils/push-datalayer'
import { Button } from '@/components/button'
import { DefaultButtonBar } from '@/components/button-bar'
import ContentWrapper from '@/components/content-wrapper'
import { FieldTextarea } from '@/components/form'
import { EditText } from '@/components/multi-row-form/form-item'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import { show as showVerifyPhoneModal } from '@/components/verify-phone-modal'
import { AutomobileFullList } from '@/features/Profile/Automobile'
import { EmergencyContactsFullList } from '@/features/Profile/EmergencyContacts'
import { EmploymentInfo } from '@/features/Profile/EmploymentInfo'
import { EmploymentReferencesFullList } from '@/features/Profile/EmploymentReferences'
import { InsuranceFullView } from '@/features/Profile/InsuranceInfo'
import { LandlordReferencesFullList } from '@/features/Profile/LandlordReferences'
import LivScore from '@/features/Profile/LivScore'
import { PetsFullList } from '@/features/Profile/Pets'
import { RoommatesFullList } from '@/features/Profile/Roommates'
import TenantBasicInfo from '@/features/Profile/TenantBasicInfo'
import VerifyIdentity from '@/features/Profile/VerifyIdentity'
import { RequiredText, PanelHeader, PanelTitle, StyledPanel } from '@/features/Profile/elements'
import useActiveApplications from '@/hooks/use-active-applications'
import useLivScore from '@/hooks/use-liv-score'
import useMediaQuery from '@/hooks/use-media-query'
import useProfile from '@/hooks/use-profile'
import useRole from '@/hooks/use-role'
import useGoogleEventUserData from '@/hooks/use-google-event-user-data'
import { select } from '@/store'
import { breakpoints, colors, fontWeights } from '@/styles'
import { isFalsy, isNullOrEmpty } from '@/utils'
import isTruthy from '@/utils/is-truthy'
import { useTrackingWithSort, EVENTS } from '@/components/tracking'
import useGetListingApplications from '@/hooks/use-get-listing-applications'
import InfoView from '../ApplicationView/info-view'

const Background = createGlobalStyle`
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    body {
      background: ${colors.white};
    }
  }
`

const TitleBase = styled.h1`
  display: block;
  font-size: 25px;
  font-weight: ${fontWeights.bold};
  padding: 50px 0 45px;
  text-align: center;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    text-align: left;
    padding-left: 20px;
    padding-bottom: 25px;
  }
`

const Title = styled(TitleBase)`
  ${props =>
    !props.autoHide &&
    `
    background: ${colors.lightGrey};
  `}
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding-top: 35px;
    padding-bottom: 10px;
  }
`

const Wrapper = styled(ContentWrapper)`
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    max-width: none;
    padding-top: 32px;
    > div {
      > div:first-of-type {
        margin: 0;
        height: 50px;
      }
    }
    h2 {
      font-size: 20px;
      line-height: 30px;
    }
    ${EditText} {
      right: 0;
    }
    ${RequiredText} {
      margin-top: 5px;
    }
    > ${Title} + div {
      > div:first-of-type {
        height: 50px;
      }
    }
  }

  ${props =>
    props.autoHide
      ? `
    > div:first-of-type {
      > div:first-of-type {
        height: 35px;
      }
    }
    `
      : `
  `}
`

const SubTitleText = styled(TitleBase)`
  padding: 0;
  margin: 80px 0 ${props => (props.noPanel ? '64' : '-24')}px;
  height: 24px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: unset;
    margin: 0;
    padding: 50px 20px 25px;
  }
  ${props =>
    !props.showLargeHeadings &&
    `
    display: none;
  `}
`

const propIsFalsy = propSatisfies(isFalsy)
const pathIsFalsy = pathSatisfies(isFalsy)

const isMissingBasicInfo = anyPass([
  propSatisfies(isNullOrEmpty, 'avatar'),
  propIsFalsy('dob'),
  propIsFalsy('about_me'),
  pathIsFalsy(['company_info', 'address']),
  pathIsFalsy(['company_info', 'city']),
  pathIsFalsy(['company_info', 'state']),
  pathIsFalsy(['company_info', 'zip']),
  pathIsFalsy(['company_info', 'country_code'])
])

const progress = key =>
  pipe(path(['profile_progress', 'liv_score', 'missing', key]), isNil, isFalsy)

const isMissingLivScore = progress('liv_verified_state')

const isMissingFinance = data => {
  const files = data.secureFiles || []
  const hasJob = () => {
    if (data.job_title === 'unemployed') return true
    const jobFiles = filter(
      propSatisfies(includes(__, ['employment_verification', 'school_verification']), 'tag'),
      files
    )
    return jobFiles.length > 0 && !!data.job_title
  }
  const hasIncome = () => {
    const incomeFiles = filter(propSatisfies(equals('income_verification'), 'tag'), files)
    return incomeFiles.length > 0
  }
  return !hasJob() || !hasIncome()
}

const isUnset = value => value === '0' || value === 0

const isIdVerifiedOrPending = anyPass([
  propEq('identity_verified_state', 'pending'),
  propEq('identity_verified_state', 'verified')
])

const isIdNotVerifiedOrPending = complement(isIdVerifiedOrPending)

const DOCUMENT_TYPES = [
  't4',
  'tax_slip',
  'bank_statement',
  'business_card',
  'job_offer',
  'payslip',
  'student_loan',
  'school_id',
  'school_letter'
]

const MOBILE_PAGES = {
  basicInfo: ['basicInfo', 'pets', 'emergency'],
  livScore: ['livScore', 'finance'],
  references: ['employment', 'id', 'landlord'],
  extraInfo: []
}

const MOBILE_PAGES_ENTRIES = ['basicInfo', 'livScore', 'references', 'extraInfo'].map(key => [
  key,
  MOBILE_PAGES[key]
])

const MOBILE_PAGE_VALUE = {
  basicInfo: 0,
  livScore: 1,
  references: 2,
  extraInfo: 3
}

const mergeOr = (a = {}, b = {}) =>
  pipe(
    toPairs,
    map(([key, value]) => [key, value || !!b[key]]),
    fromPairs
  )(a)

const EMPTY_OBJECT = {}

const ApplicationEdit = ({
  title: initialTitle,
  heading,
  showLargeHeadings = true,
  nextButton = {},
  autoHide,
  match: {
    params: { listingId }
  },
  history,
  location
}) => {
  const { t } = useTranslation()
  const styleDisplayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const usePages = false
  const SubTitle = props => <SubTitleText {...props} showLargeHeadings={showLargeHeadings} />
  const dispatch = useDispatch()
  const profile = useProfile()
  const pets = useSelector(select.profile.pets)
  const employmentReferences = useSelector(select.profile.employers)
  const landlordReferences = useSelector(state =>
    select.profile.contacts(state, {
      type: 'landlord'
    })
  )
  const emergencyContacts = useSelector(state =>
    select.profile.contacts(state, {
      type: 'emergency'
    })
  )
  const userRole = useRole()
  const userData = useGoogleEventUserData()
  const livScore = useLivScore()
  const activeApplications = useActiveApplications(listingId)
  const hasAlreadyApplied = activeApplications?.length > 0
  const state = location?.state || {}

  const secureFiles = useSelector(
    pipe(
      path(['profile', 'files', 'user_secure_files']),
      ramdaValues,
      filter(propSatisfies(includes(__, DOCUMENT_TYPES), 'document_type'))
    )
  )

  useEffect(() => {
    dispatch.listing.load(listingId)
  }, [dispatch.listing, listingId])

  useEffect(() => {
    if (hasAlreadyApplied) history.replace(`/application/${activeApplications[0].id}`)
  }, [hasAlreadyApplied, activeApplications, history])

  const listingData = useSelector(path(['listing', listingId, 'listings']))

  const validationSchema = Yup.object().shape({
    move_in_date: Yup.date()
  })

  const initialValues = {
    move_in_date: '',
    message: "Hi, I'm interested in your unit, please see my tenancy application."
  }

  const title =
    initialTitle || t('r.application.view.preview_tenancy_application.title', 'Application Preview')

  const hasData = () => typeof profile.id !== 'undefined'

  const notPetOwner = profile?.pet_owner === '0'
  const firstTimeRenter = profile?.first_time_renter === '1'
  const unemployed = profile?.job_title === 'unemployed'
  const selfEmployed = profile?.job_title === 'self_employed'
  const studentWithNoJob =
    profile?.job_title === 'student' &&
    (profile?.salary === 'No Income' || isUnset(profile?.school_side_working))
  const phoneNumberVerified = !isNullOrEmpty(profile?.phone)

  const { mutate: track } = useTrackingWithSort()

  const { data: applications } = useGetListingApplications(listingId)

  const showToast = useToast()
  const hasApplication = applications?.length > 0
  const onSubmit = async (values, { setSubmitting }) => {
    if (!phoneNumberVerified) {
      // If no phone number, block renter from submitting with verify phone modal
      return showVerifyPhoneModal({ history })
    }
    if (hasApplication) {
      showToast({
        type: 'error',
        message: 'You have already applied to this listing',
        tone: 'critical'
      })
      return
    }
    const { error, body } = await dispatch.application.create({
      ...values,
      listingId: listingId
    })
    setSubmitting(false)
    if (!error) {
      track({
        event_name: EVENTS.APPLICATION_CREATED,
        metadata: {
          listingId: listingId
        },
        referer: document.referrer
      })
      window.localStorage.removeItem(`create-application-${listingId}`)
      pushToDatalayer({
        event: 'logged-apply-submit',
        ...userData
      })
      history.push(`/chat/${body.timeline_room}`)
    }
  }
  const whatsLeft = () => ({
    basicInfo: isMissingBasicInfo(profile),
    pets: !(pets.length || notPetOwner),
    emergency: !emergencyContacts.length,
    livScore: isMissingLivScore(profile),
    finance: isMissingFinance(mergeRight(profile, { secureFiles })),
    employment: !(employmentReferences.length || unemployed || selfEmployed || studentWithNoJob),
    id: isIdNotVerifiedOrPending(profile),
    landlord: !(landlordReferences.length || firstTimeRenter)
  })
  const [info, setInfo] = useState({ ...state?.show })
  const show = info?.show || {}
  const isLastPage = !usePages || info.page === 'extraInfo'
  const findPage = keys =>
    MOBILE_PAGES_ENTRIES.find(([page, content]) => content.some(key => keys[key]))?.[0] ||
    'extraInfo'
  const showRequiredWarning = hasData() ? whatsLeft() : EMPTY_OBJECT
  const pageShow = !usePages || !info.page ? show : pick(MOBILE_PAGES[info.page], show)
  const pageRequired =
    !usePages || !info.page || isLastPage
      ? show
      : pick(MOBILE_PAGES[info.page], showRequiredWarning)
  const isMissingFields = pipe(ramdaValues, filter(isTruthy), length, val => val > 0)(pageRequired)
  const handleStep =
    (forward = true) =>
    () => {
      const page = findPage(forward ? showRequiredWarning : mergeOr(showRequiredWarning, show))
      const hasPage = !page || page === info.page || (info.page === 'extraInfo' && !forward)
      if (!usePages || hasPage) {
        history.push(`/application/create/${listingId}`)
        return
      }
      setInfo(values => ({ ...values, page }))
    }
  useEffect(() => {
    if (isEmpty(showRequiredWarning) || equals(showRequiredWarning, info.show)) return
    setInfo(({ page: initialPage, show: initial = {} }) => {
      const minPage = findPage(initial)
      const keys = showRequiredWarning
      const page =
        MOBILE_PAGE_VALUE[initialPage] >= MOBILE_PAGE_VALUE[minPage] ? initialPage : findPage(keys)
      return {
        page,
        show: keys
      }
    })
  }, [showRequiredWarning, setInfo, info])
  return hasData() ? (
    <Page>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <PageTitle>{title}</PageTitle>
      <Title autoHide={autoHide}>
        {heading || t('b.application.view.tenancy_application.title', 'Tenancy Application')}
      </Title>
      <Background />
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={autoHide ? handleStep() : onSubmit}
      >
        {({ isValid, values, isSubmitting }) => (
          <Form className="flex-full">
            <Wrapper className="mb-50" autoHide={autoHide}>
              {!autoHide && <InfoView application={values} listingId={listingId} />}
              <SubTitle>
                {t('b.application.view.tenancy_application.basic_info.title', 'Basic Info')}
              </SubTitle>
              {(!autoHide || pageShow.basicInfo) && (
                <TenantBasicInfo
                  data={profile}
                  editable
                  editProps={{
                    show: info,
                    onSuccessRedirectTo: location.pathname
                  }}
                  showRequiredWarning={showRequiredWarning.basicInfo}
                />
              )}
              {(!autoHide || pageShow.pets) && (
                <PetsFullList
                  showRequiredWarning={showRequiredWarning.pets}
                  isChecked={notPetOwner}
                />
              )}
              {(!autoHide || pageShow.emergency) && (
                <EmergencyContactsFullList showRequiredWarning={showRequiredWarning.emergency} />
              )}
              <SubTitle noPanel={true}>
                {t('b.application.view.tenancy_application.liv_score.title', 'Trust Score')}
              </SubTitle>
              {userRole === 'tenant' && (
                <LivScore
                  data={profile}
                  livScore={livScore}
                  userRole={userRole}
                  listingData={listingData}
                  editable
                  editProps={{
                    show: info,
                    onSuccessRedirectTo: location.pathname,
                    onBackRedirectTo: location.pathname
                  }}
                  showRequiredWarning={showRequiredWarning.livScore}
                  hideTitle={!autoHide}
                />
              )}
              {(!autoHide || pageShow.finance) && (
                <EmploymentInfo
                  data={profile}
                  editable
                  editProps={{
                    show: info,
                    onSuccessRedirectTo: location.pathname,
                    onBackRedirectTo: location.pathname
                  }}
                />
              )}
              <SubTitle>
                {t('b.application.view.tenancy_application.references.title', 'References')}
              </SubTitle>
              {(!autoHide || pageShow.employment) && (
                <EmploymentReferencesFullList
                  showRequiredWarning={showRequiredWarning.employment}
                />
              )}
              {autoHide && pageShow.id && (
                <VerifyIdentity showRequiredWarning={showRequiredWarning.id} />
              )}
              {(!autoHide || pageShow.landlord) && (
                <LandlordReferencesFullList
                  showRequiredWarning={showRequiredWarning.landlord}
                  isChecked={firstTimeRenter}
                />
              )}
              <SubTitle>
                {t('b.application.view.tenancy_application.extra_info.title', 'Extra Info')}
              </SubTitle>
              {(!autoHide || isLastPage) && <RoommatesFullList />}
              {(!autoHide || isLastPage) && (
                <InsuranceFullView
                  data={profile.files.user_secure_files}
                  editProps={{
                    show: info,
                    onSuccessRedirectTo: location.pathname,
                    onBackRedirectTo: location.pathname
                  }}
                />
              )}
              {(!autoHide || isLastPage) && <AutomobileFullList />}
              {!autoHide && (
                <div>
                  <PanelHeader>
                    <PanelTitle>
                      {t(
                        'r.profile.create.additional_notes.subtitle',
                        'Additional Notes / Comments'
                      )}
                    </PanelTitle>
                  </PanelHeader>
                  <StyledPanel hSpace={30} vSpace={30}>
                    <FieldTextarea name="message" fontSize={14} fontWeight={fontWeights.regular} />
                  </StyledPanel>
                </div>
              )}
            </Wrapper>
            <DefaultButtonBar handleBack={autoHide ? handleStep(false) : history.goBack}>
              <Button
                theme="primary"
                size="small"
                type="submit"
                disabled={
                  phoneNumberVerified
                    ? autoHide
                      ? isSubmitting || isMissingFields
                      : isSubmitting || isMissingFields || !isValid
                    : autoHide
                      ? isMissingFields
                      : isMissingFields || !isValid
                }
                id={
                  nextButton.clickId || (!autoHide ? `logged-apply-submit-${listingId}` : undefined)
                }
              >
                {nextButton.text ||
                  (styleDisplayMobile
                    ? t('r.application.create.apply_mobile.button', 'Send')
                    : t('r.application.create.apply.button', 'Send Application'))}
              </Button>
            </DefaultButtonBar>
            {!isSubmitting && <Persist name={`create-application-${listingId}`} />}
          </Form>
        )}
      </Formik>
    </Page>
  ) : null
}

export default ApplicationEdit
